import moment from 'moment'

/**
 * @typedef {Object} DateFilter
 * @property {string} name - The name of the context.
 * @property {string} title - The title of the context.
 * @property {Object} quickFilter - The quick filter of the context.
 * @property {Object} quickFilter.date - The date of the quick filter.
 * @property {string} quickFilter.date.filterType - The type of the filter.
 * @property {string} quickFilter.date.type - The type of the date range.
 * @property {string} quickFilter.date.dateFrom - The start date of the date range.
 * @property {string} quickFilter.date.dateTo - The end date of the date range.
 */

/**
 * @typedef {Object} DateRange
 * @property {string} dateFrom - The start date.
 * @property {string} dateTo - The end date.
 * @property {string} filterType - The type of the filter.
 * @property {string} type - The type of the date range.
 */

/**
 * @typedef {Object} FilterModel
 * @property {DateRange} date - The date range object.
 */

/**
 *
 * @param {number} index
 * @param {string} year ISO DateString
 * @returns {DateFilter}
 */
const getDateFilterForYear = (index, year) => {
  return {
    name: `year${index}`,
    title: moment(year).format('YYYY'),
    quickFilter: {
      date: {
        filterType: 'date',
        type: 'inRange',
        dateFrom: year,
        dateTo: moment(year).endOf('year').toISOString(true),
      },
    },
  }
}

export const buildYearFilters = () => {
  const years = [
    moment().add(1, 'minute').subtract(2, 'year').startOf('year').toISOString(true),
    moment().add(1, 'minute').subtract(1, 'year').startOf('year').toISOString(true),
    moment().add(1, 'minute').startOf('year').toISOString(true),
  ]

  return years.map((year, index) => (getDateFilterForYear(index, year)))
}

const buildMonthFilters = (year) => {
  const months = moment.monthsShort(moment.locale('de'))
  const allowedStartDate = year?.quickFilter?.date?.dateFrom
  const allowedMoment = year ? moment(allowedStartDate) : moment()

  return months.map((month, index) => ({
    name: `month${index}`,
    title: month,
    quickFilter: {
      date: {
        filterType: 'date',
        type: 'inRange',
        dateFrom: allowedMoment.month(index).startOf('month').toISOString(true),
        dateTo: allowedMoment.month(index).endOf('month').toISOString(true),
      },
    },
  }))
}

const buildQuarterFilters = (year) => {
  const allowedEndDate = year?.quickFilter?.date?.dateTo

  const quarters = [
    moment(allowedEndDate).quarter(1).startOf('quarter').toISOString(true),
    moment(allowedEndDate).quarter(2).startOf('quarter').toISOString(true),
    moment(allowedEndDate).quarter(3).startOf('quarter').toISOString(true),
    moment(allowedEndDate).quarter(4).startOf('quarter').toISOString(true),
  ]

  return quarters.map((quarter, index) => ({
    name: `quarter${index}`,
    title: `Q${index + 1}`,
    quickFilter: {
      date: {
        filterType: 'date',
        type: 'inRange',
        dateFrom: quarter,
        dateTo: moment(quarter).endOf('quarter').toISOString(true),
      },
    },
  }))
}

/**
 * create the FilterModel for agGrid
 * @param {{dateFilters: DateFilter[], filterModel: FilterModel}} param
 * @returns {FilterModel}
 */
const DATE_FILTER_MODEL_KEY = 'date'

const itemMatchesDateCondition = (item, condition, dateField = 'date') => {
  const date = item[dateField]
  if (!date) {
    return false
  }

  const { dateFrom, dateTo, type } = condition
  if (type === 'inRange') {
    return moment(date).isBetween(moment(dateFrom).subtract(1, 'days'), moment(dateTo).subtract(1, 'days'))
  }

  return false
}

const getDateFilterConditions = (dateFilterModel) => {
  if (!dateFilterModel) {
    return []
  }

  if (dateFilterModel.conditions?.length) {
    return dateFilterModel.conditions
  }

  if (dateFilterModel.type === 'inRange') {
    return [dateFilterModel]
  }

  return Object.keys(dateFilterModel)
    .filter(key => key.startsWith('condition'))
    .map(key => dateFilterModel[key])
}

const getMonthIndexFromFilterName = (name) => parseInt(name.replace('month', ''), 10)
const getQuarterIndexFromFilterName = (name) => parseInt(name.replace('quarter', ''), 10) + 1

const itemMatchesDateFilters = (item, dateFilters, dateField = 'date') => {
  if (!dateFilters?.length) {
    return true
  }

  const date = item[dateField]
  if (!date) {
    return false
  }

  const itemMoment = moment(date)
  const yearFilter = dateFilters.filter(filterItem => filterItem?.name.includes('year'))
  const quarterFilter = dateFilters.filter(filterItem => filterItem?.name.includes('quarter'))
  const monthFilter = dateFilters.filter(filterItem => filterItem?.name.includes('month'))
  const hasOnlyYearFilter = yearFilter.length > 0 && monthFilter.length === 0 && quarterFilter.length === 0
  const hasNoYearFilter = yearFilter.length === 0 && (monthFilter.length > 0 || quarterFilter.length > 0)

  if (hasOnlyYearFilter) {
    return yearFilter.some(filterItem => itemMatchesDateCondition(item, filterItem.quickFilter.date, dateField))
  }

  if (hasNoYearFilter) {
    const matchesMonth = monthFilter.length === 0
      || monthFilter.some(filterItem => itemMoment.month() === getMonthIndexFromFilterName(filterItem.name))
    const matchesQuarter = quarterFilter.length === 0
      || quarterFilter.some(filterItem => itemMoment.quarter() === getQuarterIndexFromFilterName(filterItem.name))

    return matchesMonth && matchesQuarter
  }

  return yearFilter.some((yearItem) => {
    const year = moment(yearItem.quickFilter.date.dateFrom).year()
    const matchesMonth = monthFilter.length === 0
      || monthFilter.some(filterItem => itemMoment.year() === year && itemMoment.month() === getMonthIndexFromFilterName(filterItem.name))
    const matchesQuarter = quarterFilter.length === 0
      || quarterFilter.some(filterItem => itemMoment.year() === year && itemMoment.quarter() === getQuarterIndexFromFilterName(filterItem.name))

    return matchesMonth && matchesQuarter
  })
}

const applyDateFiltersToItems = (items, dateFilters, dateField = 'date') => {
  if (!dateFilters?.length) {
    return items
  }

  return items.filter(item => itemMatchesDateFilters(item, dateFilters, dateField))
}

const applyDateFilterModelToItems = (items, dateFilterModel, dateField = 'date') => {
  const conditions = getDateFilterConditions(dateFilterModel)

  if (!conditions.length) {
    return items
  }

  return items.filter(item =>
    conditions.some(condition => itemMatchesDateCondition(item, condition, dateField)),
  )
}

const stripDateFromFilterModel = (filterModel) => {
  if (!filterModel) {
    return null
  }

  const { [DATE_FILTER_MODEL_KEY]: dateFilter, ...rest } = filterModel
  return Object.keys(rest).length ? rest : null
}

const getDateFilterModel = ({ dateFilters, filterModel }) => {
  if (!dateFilters.length) {
    return stripDateFromFilterModel(filterModel)
  }

  const yearFilter = dateFilters.filter(item => item && item.name.includes('year'))
  const quarterFilter = dateFilters.filter(item => item && item.name.includes('quarter'))
  const monthFilter = dateFilters.filter(item => item && item.name.includes('month'))
  const hasOnlyYearFilter = yearFilter.length > 0 && (monthFilter.length === 0 && quarterFilter.length === 0)
  const hasNoYearFilter = yearFilter.length === 0 && (monthFilter.length > 0 || quarterFilter.length > 0)

  const newFilterModelItem = {
    filterType: 'date',
    operator: 'OR',
    conditions: [],
  }

  if (hasOnlyYearFilter) {
    yearFilter.forEach((item, key) => {
      newFilterModelItem[`condition${key + 1}`] = item.quickFilter.date
      newFilterModelItem.conditions.push({ ...item.quickFilter.date })
    })
  } else if (hasNoYearFilter) {
    monthFilter.forEach((item, key) => {
      const monthIndex = getMonthIndexFromFilterName(item.name)
      newFilterModelItem[`condition${key + 1}`] = {
        filterType: 'date',
        type: 'inRange',
        dateFrom: moment().month(monthIndex).startOf('month').toISOString(true),
        dateTo: moment().month(monthIndex).endOf('month').toISOString(true),
        matchAnyYear: true,
        monthIndex,
      }
      newFilterModelItem.conditions.push(newFilterModelItem[`condition${key + 1}`])
    })

    quarterFilter.forEach((item, key) => {
      const quarterIndex = getQuarterIndexFromFilterName(item.name)
      const conditionKey = `condition${monthFilter.length + key + 1}`
      newFilterModelItem[conditionKey] = {
        filterType: 'date',
        type: 'inRange',
        dateFrom: moment().quarter(quarterIndex).startOf('quarter').toISOString(true),
        dateTo: moment().quarter(quarterIndex).endOf('quarter').toISOString(true),
        matchAnyYear: true,
        quarterIndex,
      }
      newFilterModelItem.conditions.push(newFilterModelItem[conditionKey])
    })
  } else {
    let conditionIndex = 0
    yearFilter.forEach((yearItem) => {
      [...quarterFilter, ...monthFilter].forEach((item) => {
        conditionIndex += 1
        const year = moment(yearItem.quickFilter.date.dateFrom).year()
        const condition = {
          ...item.quickFilter.date,
          dateFrom: moment(item.quickFilter.date.dateFrom).year(year).toISOString(true),
          dateTo: moment(item.quickFilter.date.dateTo).year(year).toISOString(true),
        }
        newFilterModelItem[`condition${conditionIndex}`] = condition
        newFilterModelItem.conditions.push(condition)
      })
    })
  }

  const newFilterModel = { ...stripDateFromFilterModel(filterModel), date: newFilterModelItem }

  return newFilterModel
}

const getDateFilterItems = (activeYearFilter) => {
  return {
    years: buildYearFilters(),
    months: buildMonthFilters(activeYearFilter),
    quarters: buildQuarterFilters(activeYearFilter),
  }
}

const handleDateFilters = ({ state, list, filterType, filter }) => {
  const dateFilters = [...state[list].dateFilters].filter(Boolean)
  let newFilter = state[list].dateFilterItems[`${filterType}s`]?.find(item => item?.name === filter)

  const isPresent = dateFilters.map(item => item?.name).includes(newFilter?.name)

  if (isPresent) {
    dateFilters.splice(dateFilters.findIndex(item => item?.name === newFilter?.name), 1)
    newFilter = undefined
  } else if (!isPresent && !!newFilter) {
    for (let index = dateFilters.length - 1; index >= 0; index -= 1) {
      if (dateFilters[index]?.name.includes(filterType)) {
        dateFilters.splice(index, 1)
      }
    }
    dateFilters.push(newFilter)
  }

  const filterModel = getDateFilterModel({
    dateFilters,
    filterModel: stripDateFromFilterModel(state[list].filterModel),
  })
  const yearFilter = dateFilters.find(item => item?.name.includes('year')) || undefined
  const updatedFilterItems = getDateFilterItems(filterType === 'year' ? newFilter : yearFilter)

  return {
    dateFilters: dateFilters.filter(Boolean),
    filterModel,
    updatedFilterItems,
  }
}

export {
  DATE_FILTER_MODEL_KEY,
  applyDateFilterModelToItems,
  applyDateFiltersToItems,
  getDateFilterItems,
  getDateFilterModel,
  handleDateFilters,
  itemMatchesDateFilters,
}
